import { ScheduleRule, ScheduleSpacing } from '../../../scheduling/schedule.rule';

/**
 * Plan section 19 — how Service history judges a task's completions. Reporting only: it never moves a
 * date, so it is separate from after-service leeway (section 15), which stays a scheduling setting.
 *
 * `None` is the editor's answer for "no rule" and is never stored: absence is the stored form, so a
 * task saved before rules existed and one saved with None read the same.
 */
export enum PlannedTaskReportRuleKind {
    None = 'None',
    Tolerance = 'Tolerance',
    PeriodTarget = 'PeriodTarget',
}

export enum PlannedTaskReportPeriod {
    Week = 'Week',
    Month = 'Month',
    Quarter = 'Quarter',
}

/** Days either side of the occurrence's identity date (`localDate`), never of the approved date. */
export interface PlannedTaskToleranceRule {
    kind: PlannedTaskReportRuleKind.Tolerance;
    daysBefore: number;
    daysAfter: number;
}

/** N completions per calendar period, optionally spaced; spacing is the only place `ScheduleSpacing` is authored. */
export interface PlannedTaskPeriodTargetRule {
    kind: PlannedTaskReportRuleKind.PeriodTarget;
    count: number;
    period: PlannedTaskReportPeriod;
    spacing?: ScheduleSpacing;
}

export type PlannedTaskReportRule = PlannedTaskToleranceRule | PlannedTaskPeriodTargetRule;

/** An editor's answer: every kind's fields at once, so switching kind keeps what was typed. */
export interface PlannedTaskReportRuleDraft {
    kind?: PlannedTaskReportRuleKind;
    daysBefore?: number;
    daysAfter?: number;
    count?: number;
    period?: PlannedTaskReportPeriod;
    spacing?: ScheduleSpacing;
}

export const PLANNED_TASK_REPORT_RULE_KIND_LABELS: Readonly<Record<PlannedTaskReportRuleKind, string>> = {
    [PlannedTaskReportRuleKind.None]: 'None',
    [PlannedTaskReportRuleKind.Tolerance]: 'Tolerance',
    [PlannedTaskReportRuleKind.PeriodTarget]: 'Period target',
};

export const PLANNED_TASK_REPORT_PERIOD_LABELS: Readonly<Record<PlannedTaskReportPeriod, string>> = {
    [PlannedTaskReportPeriod.Week]: 'Week',
    [PlannedTaskReportPeriod.Month]: 'Month',
    [PlannedTaskReportPeriod.Quarter]: 'Quarter',
};

/** The editor's choice for a stored rule: absent reads as None. */
export function plannedTaskReportRuleKind(rule: PlannedTaskReportRule | null | undefined): PlannedTaskReportRuleKind {

    return rule?.kind === PlannedTaskReportRuleKind.Tolerance || rule?.kind === PlannedTaskReportRuleKind.PeriodTarget ? rule.kind : PlannedTaskReportRuleKind.None;
}

/**
 * Every reason `rule` cannot be stored, empty when it can. Null and absent are "no rule" and valid.
 * Whole days of zero or more, because a fractional day has no calendar meaning in a report.
 */
export function validateReportRule(rule: unknown): string[] {

    if (rule === null || rule === undefined) return [];
    if (!isPlainObject(rule)) return ['A report rule must be Tolerance or Period target'];
    if (rule['kind'] === PlannedTaskReportRuleKind.Tolerance) {
        return [
            ...(isWholeDays(rule['daysBefore']) ? [] : ['Days before must be a whole number of zero or more']),
            ...(isWholeDays(rule['daysAfter']) ? [] : ['Days after must be a whole number of zero or more']),
        ];
    }
    if (rule['kind'] === PlannedTaskReportRuleKind.PeriodTarget) {
        return [
            ...(isWholeDays(rule['count']) && (rule['count'] as number) > 0 ? [] : ['Completions per period must be a whole number of one or more']),
            ...(Object.values(PlannedTaskReportPeriod).includes(rule['period'] as PlannedTaskReportPeriod) ? [] : ['Period must be Week, Month or Quarter']),
            ...spacingIssues(rule['spacing']),
        ];
    }

    return ['A report rule must be Tolerance or Period target'];
}

/**
 * The stored form of a rule: only the fields its kind owns, null for None. Unset spacing bounds drop
 * out, so the completion stamp's partial index never sees an empty rule as a rule.
 */
export function normaliseReportRule(rule: PlannedTaskReportRuleDraft | null | undefined): PlannedTaskReportRule | null {

    const draft = rule ?? {};
    if (draft.kind === PlannedTaskReportRuleKind.Tolerance) return { kind: draft.kind, daysBefore: draft.daysBefore ?? 0, daysAfter: draft.daysAfter ?? 0 };
    if (draft.kind !== PlannedTaskReportRuleKind.PeriodTarget) return null;
    const spacing = reportSpacingOf(draft.spacing);

    return { kind: draft.kind, count: draft.count ?? 1, period: draft.period ?? PlannedTaskReportPeriod.Month, ...(spacing ? { spacing } : {}) };
}

/**
 * The editor's starting answer: the stored rule over prefilled defaults (tolerance from the schedule's
 * frequency, one completion a month), so choosing a kind shows sensible numbers. Prefill only: nothing
 * is stored until the user saves a kind.
 */
export function reportRuleDraft(rule: PlannedTaskReportRule | null | undefined, schedule: ScheduleRule | null | undefined): PlannedTaskReportRuleDraft {

    return { ...defaultReportTolerance(schedule), count: 1, period: PlannedTaskReportPeriod.Month, ...rule, spacing: { ...(rule?.kind === PlannedTaskReportRuleKind.PeriodTarget ? rule.spacing : {}) } };
}

/** A period target's spacing with only the bounds that are set; undefined for any other rule. */
export function reportRuleSpacing(rule: PlannedTaskReportRule | null | undefined): ScheduleSpacing | undefined {

    return rule?.kind === PlannedTaskReportRuleKind.PeriodTarget ? reportSpacingOf(rule.spacing) : undefined;
}

/** Days before / after a due date by how often the task falls due. Prefill only: the stored value is whatever is saved. */
export function defaultReportTolerance(schedule: ScheduleRule | null | undefined): Pick<PlannedTaskToleranceRule, 'daysBefore' | 'daysAfter'> {

    switch (scheduleCadence(schedule)) {
        case 'weekly': return { daysBefore: 1, daysAfter: 2 };
        case 'monthly': return { daysBefore: 3, daysAfter: 7 };
        case 'quarterly': return { daysBefore: 14, daysAfter: 14 };
        case 'yearly': return { daysBefore: 30, daysAfter: 30 };
        default: return { daysBefore: 0, daysAfter: 0 };
    }
}

/** `[due − before, due + after]` as local dates, resolved once when the Visit row is stored or approved. */
export function toleranceBounds(dueDate: string, rule: Pick<PlannedTaskToleranceRule, 'daysBefore' | 'daysAfter'>): { from: string; to: string } {

    return { from: addLocalDays(dueDate, -rule.daysBefore), to: addLocalDays(dueDate, rule.daysAfter) };
}

/**
 * The calendar period a local date falls in: `2026-W41` (ISO week), `2026-10`, `2026-Q4`. Keys sort in
 * time order within one period kind, which is all a report groups by.
 */
export function reportPeriodKey(localDate: string, period: PlannedTaskReportPeriod): string {

    const [year, month] = [Number(localDate.slice(0, 4)), Number(localDate.slice(5, 7))];
    if (period === PlannedTaskReportPeriod.Month) return localDate.slice(0, 7);
    if (period === PlannedTaskReportPeriod.Quarter) return `${year}-Q${Math.ceil(month / 3)}`;
    const { isoYear, isoWeek } = isoWeekOf(localDate);

    return `${isoYear}-W${String(isoWeek).padStart(2, '0')}`;
}

type ScheduleCadence = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | undefined;

/** Absent is the working-day default, which is daily; per-visit and date lists have no cadence of their own. */
function scheduleCadence(schedule: ScheduleRule | null | undefined): ScheduleCadence {

    if (!schedule) return 'daily';
    if (schedule.kind === 'calendar') {
        switch (schedule.calendar?.repeat) {
            case 'daily': return 'daily';
            case 'weekly':
            case 'fortnightly': return 'weekly';
            case 'monthly': return 'monthly';
            case 'quarterly': return 'quarterly';
            case 'yearly': return 'yearly';
            default: return undefined;
        }
    }
    if (schedule.kind === 'afterService' && schedule.afterService) {
        const { interval, unit } = schedule.afterService;
        const days = unit === 'years' ? interval * 365 : unit === 'months' ? interval * 30 : interval;
        if (days <= 1) return 'daily';
        if (days <= 14) return 'weekly';
        if (days <= 60) return 'monthly';
        if (days < 365) return 'quarterly';
        return 'yearly';
    }

    return undefined;
}

function reportSpacingOf(spacing: ScheduleSpacing | null | undefined): ScheduleSpacing | undefined {

    const min = typeof spacing?.minDays === 'number' ? { minDays: spacing.minDays } : {};
    const max = typeof spacing?.maxDays === 'number' ? { maxDays: spacing.maxDays } : {};

    return Object.keys(min).length || Object.keys(max).length ? { ...min, ...max } : undefined;
}

function spacingIssues(spacing: unknown): string[] {

    if (spacing === undefined || spacing === null) return [];
    if (!isPlainObject(spacing)) return ['Spacing must be a minimum and/or maximum number of days'];
    const issues = [
        ...(spacing['minDays'] == null || isWholeDays(spacing['minDays']) ? [] : ['Minimum days between visits must be a whole number of zero or more']),
        ...(spacing['maxDays'] == null || isWholeDays(spacing['maxDays']) ? [] : ['Maximum days between visits must be a whole number of zero or more']),
    ];
    if (!issues.length && typeof spacing['minDays'] === 'number' && typeof spacing['maxDays'] === 'number' && spacing['minDays'] > spacing['maxDays']) {
        issues.push('The most days between visits cannot be less than the least');
    }

    return issues;
}

function isWholeDays(value: unknown): boolean {

    return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {

    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function addLocalDays(localDate: string, days: number): string {

    const date = new Date(`${localDate}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + days);

    return date.toISOString().slice(0, 10);
}

function isoWeekOf(localDate: string): { isoYear: number; isoWeek: number } {

    const date = new Date(`${localDate}T00:00:00Z`);
    const weekday = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - weekday);
    const isoYear = date.getUTCFullYear();
    const yearStart = Date.UTC(isoYear, 0, 1);

    return { isoYear, isoWeek: Math.ceil(((date.getTime() - yearStart) / 86_400_000 + 1) / 7) };
}
